import { memo, useCallback, useState } from 'react';
import { Card, Container } from 'react-bootstrap';

import { ErrorAlert, LoadingSpinner, Pagination } from '@/components/common';
import {
  OrganizationFilters,
  OrganizationsTable,
  StatsWidget,
} from '@/components/organizations';
import { useOrganizations, useOrganizationStats } from '@/hooks';
import type {
  OrganizationFilters as OrganizationFiltersType,
  OrganizationSortableField,
} from '@/types';

const DEFAULT_FILTERS: OrganizationFiltersType = {
  min_confidence: 0,
  min_facility_count: 1,
  sort_by: 'facility_count',
  sort_order: 'desc',
};

export const OrganizationsPage = memo(function OrganizationsPage() {
  const [filters, setFilters] =
    useState<OrganizationFiltersType>(DEFAULT_FILTERS);
  const { stats, loading: statsLoading } = useOrganizationStats();
  const {
    organizations,
    page,
    pageSize,
    loading,
    error,
    refetch,
    setPage,
    setFilters: updateFilters,
  } = useOrganizations(filters);

  const handleFiltersChange = useCallback(
    (newFilters: OrganizationFiltersType) => {
      setFilters(newFilters);
      updateFilters(newFilters);
    },
    [updateFilters]
  );

  const handleSort = useCallback(
    (field: OrganizationSortableField) => {
      let newOrder: 'asc' | 'desc' = field === 'name' ? 'asc' : 'desc';

      if (filters.sort_by === field) {
        // Toggle order if same field
        newOrder = filters.sort_order === 'asc' ? 'desc' : 'asc';
      }

      handleFiltersChange({
        ...filters,
        sort_by: field,
        sort_order: newOrder,
      });
    },
    [filters, handleFiltersChange]
  );

  // Estimate total for pagination (API returns array, no total count)
  const estimatedTotal =
    organizations.length < pageSize
      ? (page - 1) * pageSize + organizations.length
      : page * pageSize + 1;

  return (
    <Container fluid className="py-4">
      <h1 className="h3 mb-4">Organizations</h1>

      <StatsWidget stats={stats} loading={statsLoading} />

      <OrganizationFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        loading={loading}
      />

      {error && <ErrorAlert message={error} onRetry={refetch} />}

      {loading && !organizations.length ? (
        <LoadingSpinner message="Loading organizations..." />
      ) : (
        <Card>
          <Card.Body className="p-0">
            <OrganizationsTable
              organizations={organizations}
              sortField={filters.sort_by}
              sortOrder={filters.sort_order}
              onSort={handleSort}
            />
          </Card.Body>
          <Card.Footer>
            <Pagination
              currentPage={page}
              totalItems={estimatedTotal}
              pageSize={pageSize}
              onPageChange={setPage}
              disabled={loading}
            />
          </Card.Footer>
        </Card>
      )}
    </Container>
  );
});
